import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loader2, Inbox, Calendar, Compass, FolderOpen, FileCheck, Clock, Award, ExternalLink, UserPlus, Activity, Bell } from "lucide-react";

const STATUSES = ["New Intake", "Consult Scheduled", "Active Strategy", "Gathering Evidence", "Ready to File", "Filed and Waiting", "Decision Received"];
const ICONS = {
  "New Intake": Inbox,
  "Consult Scheduled": Calendar,
  "Active Strategy": Compass,
  "Gathering Evidence": FolderOpen,
  "Ready to File": FileCheck,
  "Filed and Waiting": Clock,
  "Decision Received": Award,
};

export default function Dashboard() {
  const { user } = useAuth();
  const [vets, setVets] = useState(null);
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const [v, s, l] = await Promise.all([
      api.get("/veterans"),
      api.get("/stats"),
      api.get("/admin/stats/live").catch(() => ({ data: null })),
    ]);
    setVets(v.data);
    setStats(s.data);
    setLive(l.data);
  };
  useEffect(() => {
    load();
    // Refresh live stats every 60s while admin is on the page
    const iv = setInterval(async () => {
      try { const { data } = await api.get("/admin/stats/live"); setLive(data); }
      catch (err) { console.warn("Live stats refresh failed:", err); }
    }, 60_000);
    return () => clearInterval(iv);
  }, []);

  const onDrop = async (status) => {
    if (!dragId) return;
    await api.patch(`/veterans/${dragId}/status`, { status });
    setDragId(null);
    load();
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-editorial text-3xl text-primary">Admin only.</h1>
        <p className="text-muted-foreground mt-3">You must sign in as the admin to access this dashboard.</p>
        <Link to="/login" className="mt-6 inline-block text-primary link-underline" data-testid="admin-signin-link">Go to sign-in →</Link>
      </div>
    );
  }

  if (!vets) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-[1600px] px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-caps text-primary/70">Admin</div>
          <h1 className="font-display text-4xl text-primary tracking-tight mt-2">Veteran pipeline</h1>
          <p className="text-primary/70 text-sm mt-1">Drag cards between columns to update status.</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <Link to="/admin/veterans" className="inline-flex items-center gap-2 h-11 px-4 border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground" data-testid="admin-veterans-link">
            Table view
          </Link>
          <Link to="/admin/articles" className="inline-flex items-center gap-2 h-11 px-4 border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground" data-testid="admin-articles-link">
            Articles
          </Link>
          <Link to="/admin/claims" className="inline-flex items-center gap-2 h-11 px-4 border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground" data-testid="admin-claims-link">
            Claims
          </Link>
          <Link to="/admin/rates" className="inline-flex items-center gap-2 h-11 px-5 bg-accent text-accent-foreground font-semibold hover:bg-accent/90" data-testid="admin-rates-link">
            Manage Rates
          </Link>
        </div>
      </div>

      {/* Live status — morning glance */}
      {live && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-live-status">
          <div className="p-6 bg-primary text-primary-foreground border-l-4 border-accent">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-accent" strokeWidth={2} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary-foreground/70">New intakes today</div>
            </div>
            <div className="font-display text-5xl mt-3 leading-none" data-testid="live-new-intakes">{live.new_intakes_today}</div>
            <div className="text-xs text-primary-foreground/60 mt-2 font-mono">since midnight UTC</div>
          </div>

          <div className="p-6 bg-white border-2 border-primary/20">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" strokeWidth={2} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary/70">Active claims</div>
            </div>
            <div className="font-display text-5xl mt-3 leading-none text-primary" data-testid="live-active-claims">{live.active_claims}</div>
            <div className="text-xs text-primary/60 mt-2 font-mono">not yet in outcome phase</div>
          </div>

          <div className={`p-6 border-2 ${live.waiting_on_vde > 0 ? "bg-accent/20 border-accent" : "bg-secondary border-border"}`}>
            <div className="flex items-center gap-2">
              <Bell className={`w-4 h-4 ${live.waiting_on_vde > 0 ? "text-accent-foreground" : "text-primary"}`} strokeWidth={2} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary/80">Waiting on VDE</div>
            </div>
            <div className="font-display text-5xl mt-3 leading-none text-primary" data-testid="live-waiting-vde">{live.waiting_on_vde}</div>
            <div className="text-xs text-primary/60 mt-2 font-mono">
              needs you · VA has {live.waiting_on_va} · Vet has {live.waiting_on_veteran}
            </div>
          </div>
        </div>
      )}

      <div className="hidden md:flex gap-8 mb-8 bg-secondary p-6 border-l-4 border-accent">
        {STATUSES.map((s) => (
          <div key={s}>
            <div className="label-caps text-primary/70">{s}</div>
            <div className="font-display text-3xl text-primary" data-testid={`stat-${s.toLowerCase()}`}>{stats?.by_status?.[s] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {STATUSES.map((s) => {
          const items = vets.filter((v) => v.status === s);
          const Icon = ICONS[s];
          return (
            <div
              key={s}
              className="bg-muted/40 border border-border rounded-none min-h-[400px]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s)}
              data-testid={`col-${s.toLowerCase()}`}
            >
              <div className="p-4 border-b border-border flex items-center justify-between bg-card">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-accent" strokeWidth={1.6} />
                  <div className="font-medium text-sm">{s}</div>
                </div>
                <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
              </div>
              <div className="p-3 space-y-3">
                {items.map((v) => (
                  <Card
                    key={v.veteran_id}
                    className="p-4 rounded-none shadow-none border-border cursor-grab hover:-translate-y-0.5 hover:shadow-sm transition-transform"
                    draggable
                    onDragStart={() => setDragId(v.veteran_id)}
                    data-testid={`vet-card-${v.veteran_id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-sm text-primary">{v.full_name}</div>
                      <Link to={`/admin/veteran/${v.veteran_id}`} className="text-muted-foreground hover:text-primary">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{v.branch} · {v.mos || "MOS n/a"}</div>
                    <div className="mt-3 flex items-center flex-wrap gap-1.5">
                      {v.current_rating > 0 && <Badge variant="outline" className="rounded-none text-[10px] font-mono">{v.current_rating}%</Badge>}
                      {v.combat_deployed && <Badge className="rounded-none bg-accent hover:bg-accent text-[10px]">Combat</Badge>}
                      {v.has_medical_records && <Badge variant="secondary" className="rounded-none text-[10px]">Records</Badge>}
                      {v.email_status === "failed" && <Badge variant="destructive" className="rounded-none text-[10px]" data-testid={`vet-email-failed-${v.veteran_id}`}>Email failed</Badge>}
                    </div>
                    <div className="mt-3 text-[11px] text-muted-foreground font-mono">
                      {new Date(v.created_at).toLocaleDateString()}
                    </div>
                  </Card>
                ))}
                {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
